import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { rpc, useAction, useOrg, useSession } from "@vixio/data";
import { Button, Checkbox, EmptyState, Menu, PageHeader, Panel, Skeleton, formatDateTime } from "@vixio/ui";
import {
  CATEGORY_LABEL, usePortalMe, useNotificationsSummary, type NotificationCategory, type PortalNotification,
} from "./api";

const ALL: NotificationCategory[] = ["approval", "finance", "document", "ticket", "file"];

function NotificationItem({ n, onOpen }: { n: PortalNotification; onOpen: (n: PortalNotification) => void }) {
  return (
    <li className={`vx-notes__item${n.read ? "" : " vx-notes__item--unread"}`}>
      <span className="vx-notes__dot" aria-hidden="true" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <p className="vx-notes__title">
          {n.link ? (
            <Link to={n.link} onClick={() => onOpen(n)}>
              {n.title}
            </Link>
          ) : (
            n.title
          )}
          {n.read ? null : <span className="sr-only"> (não lido)</span>}
        </p>
        {n.body ? <p className="vx-notes__body">{n.body}</p> : null}
        <p className="vx-notes__meta">
          {CATEGORY_LABEL[n.category]} · {formatDateTime(n.created_at)}
        </p>
      </div>
    </li>
  );
}

/** Avisos da conta e preferências de e-mail. */
export function NotificationsPage() {
  const org = useOrg();
  const me = usePortalMe(org.id).data;
  const [limit, setLimit] = useState(30);
  const q = useQuery({
    queryKey: ["portal", "notifications", "list", org.id, limit],
    queryFn: () => rpc("portal", "my_notifications", { p_org: org.id, p_limit: limit }),
  });
  const markAll = useAction(() => rpc("portal", "mark_notifications_read", { p_org: org.id, p_ids: null }), {
    invalidate: [["portal", "notifications"]],
    success: (n) => (n ? "Avisos marcados como lidos" : "Nada novo para marcar"),
  });
  const markOne = useAction((id: string) => rpc("portal", "mark_notifications_read", { p_org: org.id, p_ids: [id] }), {
    invalidate: [["portal", "notifications"]],
    success: null,
  });
  const items = q.data?.items ?? [];
  const unread = q.data?.unread ?? 0;
  return (
    <>
      <PageHeader
        title="Avisos"
        description="O que mudou para você: entregas para aprovar, faturas, documentos, respostas nos chamados e arquivos novos."
        actions={
          <Button icon={<CheckCheck />} onClick={() => markAll.mutate(undefined)} loading={markAll.isPending} disabled={unread === 0}>
            Marcar todos como lidos
          </Button>
        }
      />
      <div className="vx-grid vx-grid--sidebar">
        <div>
          {q.isLoading ? (
            <Skeleton lines={5} />
          ) : items.length === 0 ? (
            <Panel>
              <EmptyState title="Nenhum aviso ainda">Quando houver algo para você, aparece aqui e no sino do topo.</EmptyState>
            </Panel>
          ) : (
            <>
              <ul className="vx-notes" aria-label="Avisos">
                {items.map((n) => (
                  <NotificationItem key={n.id} n={n} onOpen={(x) => !x.read && markOne.mutate(x.id)} />
                ))}
              </ul>
              {items.length >= limit ? (
                <div className="vx-row vx-section" style={{ justifyContent: "center" }}>
                  <Button onClick={() => setLimit((l) => Math.min(100, l + 30))} disabled={limit >= 100}>
                    Ver mais
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
        <PrefsPanel role={me?.role} />
      </div>
    </>
  );
}

function PrefsPanel({ role }: { role?: string }) {
  const org = useOrg();
  const q = useQuery({ queryKey: ["portal", "notifications", "prefs", org.id], queryFn: () => rpc("portal", "my_notification_prefs", { p_org: org.id }) });
  const [enabled, setEnabled] = useState(true);
  const [cats, setCats] = useState<NotificationCategory[]>(ALL);
  useEffect(() => {
    if (q.data) {
      setEnabled(q.data.email_enabled);
      setCats(q.data.categories);
    }
  }, [q.data]);
  const save = useAction(() => rpc("portal", "set_notification_prefs", { p_org: org.id, p_email_enabled: enabled, p_categories: cats }), {
    invalidate: [["portal", "notifications", "prefs"]],
    success: "Preferências salvas",
  });
  const visible = ALL.filter((c) => (c !== "finance" || role !== "viewer") && (c !== "approval" || role === "responsible"));
  return (
    <Panel title="Avisos por e-mail">
      {q.isLoading ? (
        <Skeleton lines={3} />
      ) : (
        <form
          className="vx-stack"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(undefined);
          }}
        >
          <p className="text-2" style={{ margin: 0, fontSize: "var(--fs-13)" }}>
            Enviamos para <strong>{q.data?.email}</strong>. Os avisos continuam aqui no portal de qualquer jeito.
          </p>
          <Checkbox label="Receber avisos por e-mail" checked={enabled} onChange={setEnabled} />
          <fieldset className="vx-fieldset" disabled={!enabled}>
            <legend className="vx-field__label">Quais avisos</legend>
            {visible.map((c) => (
              <Checkbox
                key={c}
                label={CATEGORY_LABEL[c]}
                checked={cats.includes(c)}
                onChange={(on) => setCats((cur) => (on ? [...new Set([...cur, c])] : cur.filter((x) => x !== c)))}
              />
            ))}
          </fieldset>
          <div className="vx-form-actions">
            <Button type="submit" variant="primary" loading={save.isPending}>
              Salvar preferências
            </Button>
          </div>
        </form>
      )}
    </Panel>
  );
}

/** Sino na barra do portal: não lidos e os avisos mais recentes. */
export function NotificationBell() {
  const session = useSession();
  const navigate = useNavigate();
  const orgId = session.org?.id ?? "";
  const q = useNotificationsSummary(orgId);
  if (!orgId || q.error) return null;
  const unread = q.data?.unread ?? 0;
  const latest = q.data?.latest ?? [];
  const label = unread > 0 ? `Avisos: ${unread} não lido${unread > 1 ? "s" : ""}` : "Avisos";
  return (
    <Menu
      label={unread > 0 ? `${unread} não lido${unread > 1 ? "s" : ""}` : "Nenhum aviso novo"}
      trigger={
        <button type="button" className="vx-btn vx-btn--ghost vx-btn--icon vx-bell" aria-label={label} title={label}>
          <Bell aria-hidden="true" />
          {unread > 0 ? (
            <span className="vx-bell__count" aria-hidden="true">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </button>
      }
      items={[
        ...latest.slice(0, 5).map((n) => ({
          label: `${n.read ? "" : "● "}${n.title}`,
          onSelect: () => {
            if (!n.read) void rpc("portal", "mark_notifications_read", { p_org: orgId, p_ids: [n.id] }).then(() => q.refetch());
            navigate(n.link ?? "/portal/avisos");
          },
        })),
        { label: "Ver todos os avisos", separatorBefore: latest.length > 0, onSelect: () => navigate("/portal/avisos") },
      ]}
    />
  );
}
